<template>
  <div v-if="shape.canUseIFTTT" class="pb-6 relative mt-2.5 w-full">
    <!-- Widget 条件切换动效 -->
    <WidgetCondition
      v-if="shape.canUseWidgetCondition"
      :shape="shape"
    />

    <!-- 普通 IFTTT 动效 -->
    <template v-else>
    <a-tabs
      v-if="ifttts.length > 0"
      v-model:activeKey="activeKey"
      type="card"
      size="small"
    >
      <a-tab-pane
        v-for="(ifttt, index) in ifttts"
        :key="ifttt.id"
        :tab="'动效' + (index + 1)"
      >
        <div class="-mt-3">
          <!-- 触发方式 -->
          <IFTTTCondition :ifttt="ifttt" />
          <!-- 动画过程 -->
          <IFTTTAnimations :ifttt="ifttt" />

          <div
            class="my-2 px-2.5 flex items-start flex-col w-full col-end-6"
          >
            <div
              class="mt-1 break-words w-full text-sm opacity-70 relative"
              :title="'空状态图片'"
            >
              <div class="">
                <!-- 是否为默认值 -->
                <span>{{ '空状态图片' }}</span>
                <span class="">
                  <a-tooltip>
                    <template #title>
                      <div>上传空状态时显示的图片。</div>
                    </template>
                    <icon
                      icon="help.svg"
                      :width="14"
                      :height="14"
                      fill="none"
                    />
                  </a-tooltip>
                </span>
              </div>
            </div>
            <ImagePicker
              :size="48"
              :icon-size="20"
              :border-color="'rgba(255, 255, 255, 0.2)'"
              :img="images[0]"
              @delete="() => onDeleteEmptyImage()"
              @change="(image:string)=>uploadEmptyImage(image)"
            />
          </div>
        </div>
      </a-tab-pane>
    </a-tabs>

    <!-- 保持可以创建多个动效 -->
    <a-button
      v-else
      class="!w-full"
      type="text"
      @click="() => onAddOrDel('', 'add')"
      >添加动效</a-button
    >
    </template>
  </div>
  <div v-else>
    <div class="text-center mt-3 opacity-70">
      <!-- 组或容器内子元素不支持 -->
      <span v-if="shape.parent">组或容器内子元素不支持设置动效</span>
      <!-- 组或容器不支持情况 -->
      <span v-else>该元素不支持动效</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ElectronApi } from '@/services/electron.api';
import { Shape } from '@/services/shape';
import { useEditorStore } from '@/store/use-editor-store';
import { useIFTTTStore } from '@/store/use-ifttt-store';
import { Modal } from 'ant-design-vue';
import { computed, ref, watch } from 'vue';

const editorStore = useEditorStore();
const iftttStore = useIFTTTStore();
const shape = computed(() => editorStore.activeShape as Shape);
const ifttts = computed(() => {
  return (
    iftttStore.ifttts(editorStore.themeId, editorStore.activeShape?.id ?? '') ??
    []
  );
});

const onAddOrDel = (
  targetKey: string | PointerEvent,
  action: 'add' | 'remove'
) => {
  if (action === 'add') {
    // 判断数量
    if (ifttts?.value?.length >= 3) {
      Modal.warning({
        title: '不可添加',
        content: '最大可添加3个动效，无法继续添加',
        okText: '知道了'
      });
      return;
    }
    const ifttt = iftttStore.addIFTTT(editorStore.themeId, shape.value.id);
    // 切换到新建的tab
    activeKey.value = ifttt?.id;
  } else if (action === 'remove') {
    const iftttId = targetKey.toString();
    iftttStore.deleteIFTTT(editorStore.themeId, { iftttId });
    activeKey.value = undefined;
  }
};

const images = computed(() => {
  return shape.value.getResources({
    resourceName: 'iftttEmptyImage',
    colorGroup: editorStore.themeColor?.id ?? '',
    base64: true
  });
});

const onDeleteEmptyImage = () => {
  shape.value.deleteResource({
    resourceName: 'iftttEmptyImage',
    colorGroup: editorStore.themeColor?.id ?? ''
  });
};

const uploadEmptyImage = async (realpath: string) => {
  const path = ElectronApi.path;
  await shape.value.uploadResource({
    resourceName: 'iftttEmptyImage',
    colorGroup: editorStore.themeColor?.id ?? '',
    realpath,
    index: 0,
    dirname: path?.basename(path?.dirname(realpath))
  });
};

const activeKey = ref();
watch(ifttts, (list, prev) => {
  const ids = (list ?? []).map((i) => i.id);
  const prevLen = prev?.length ?? 0;
  const currLen = ids.length;
  if (!currLen) {
    activeKey.value = undefined;
    return;
  }
  if (currLen > prevLen) {
    activeKey.value = ids[currLen - 1];
    return;
  }
  if (!ids.includes(activeKey.value)) {
    activeKey.value = ids[0];
  }
});
</script>

<style lang="less" scoped>
:deep(.ant-tabs) {
  padding-bottom: 10px;
  overflow: visible;
}
</style>
